import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, FileCheck, RefreshCw } from 'lucide-react';
import type {
  ApprovalRequest,
  ApprovalRequestDecision,
  ApprovalRequestStatus,
} from '../api/approvalRequests';
import {
  useApprovalRequests,
  useDecideApprovalRequest,
  useWithdrawApprovalRequest,
} from '../hooks/useApprovalRequests';
import {
  DECISION_REASONS,
  canDecide,
  describeOperation,
  effectiveStatus,
  expiryCountdown,
  statusChip,
} from '../pages/approvalRequests.logic';
import { diffRevision, revisionDecisionGate } from '../pages/contentRevisions.logic';
import { useContentRevision } from '../hooks/useContentRevision';
import { useAuth } from './AuthProvider';
import RevisionDiff from './RevisionDiff';
import { DetailDrawer, QueueTable, ReasonActionBar, StatusChip, type QueueColumn } from './ui';

const STATUS_FILTERS: readonly ApprovalRequestStatus[] = [
  'PENDING',
  'APPROVED',
  'COMPLETED',
  'REJECTED',
  'EXPIRED',
  'WITHDRAWN',
];
type StatusFilter = ApprovalRequestStatus | 'all';

function OperationChip({ operation }: { operation: string }) {
  const info = describeOperation(operation);
  return <StatusChip label={info.label} tone={info.tone} />;
}

function StatusCell({ request, nowIso }: { request: ApprovalRequest; nowIso: string }) {
  const chip = statusChip(effectiveStatus(request, nowIso));
  return <StatusChip label={chip.label} tone={chip.tone} />;
}

function ExpiryCell({ request, nowIso }: { request: ApprovalRequest; nowIso: string }) {
  const countdown = expiryCountdown(request, nowIso);
  if (!countdown) return <span className="text-gray-text">—</span>;
  return <StatusChip label={countdown.label} tone={countdown.tone} />;
}

/**
 * DM-6437: the second operator's side of content, experiment and CDN
 * approvals. Sits under the pending-actions queue on the Approvals page so
 * one inbox covers both approval systems.
 */
export default function ApprovalRequestQueue() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('PENDING');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { user, hasRole } = useAuth();
  const viewerIsOperator = hasRole('admin', 'admin-operator');
  const queue = useApprovalRequests(statusFilter === 'all' ? undefined : statusFilter);
  const decideMutation = useDecideApprovalRequest();
  const withdrawMutation = useWithdrawApprovalRequest();

  const nowIso = new Date().toISOString();
  // The API filters on the stored status; an overdue PENDING row still comes
  // back under PENDING, so the time-aware status decides what the filter shows.
  const rows = (queue.data ?? []).filter(
    request => statusFilter === 'all' || effectiveStatus(request, nowIso) === statusFilter
  );
  const selected = (queue.data ?? []).find(request => request.id === selectedId) ?? null;
  const gate = selected
    ? canDecide({ request: selected, viewerSub: user?.sub, viewerIsOperator, nowIso })
    : null;
  const isOwnRequest = selected !== null && user !== null && selected.requester_id === user.sub;
  const selectedStatus = selected ? effectiveStatus(selected, nowIso) : null;

  // DM-7214: a revision request is decided on its content. The diff comes
  // from the server, and approving waits for it and refuses a stale one.
  const revisionDetail = useContentRevision(selected?.revision);
  const revisionGate = revisionDecisionGate({
    hasRevision: selected?.revision !== undefined,
    loading: revisionDetail.isLoading,
    failed: revisionDetail.isError,
    stale: revisionDetail.data?.stale ?? false,
    changeCount: revisionDetail.data
      ? diffRevision(revisionDetail.data.base, revisionDetail.data.revision.proposal).length
      : 0,
  });
  const decisionReasons = revisionGate.allowApprove
    ? DECISION_REASONS
    : DECISION_REASONS.filter(reason => reason.value !== 'approved');

  const closeDrawer = () => setSelectedId(null);

  const submitDecision = (decision: string, note: string | undefined) => {
    if (!selected) return;
    decideMutation.mutate(
      { id: selected.id, decision: decision as ApprovalRequestDecision, note },
      { onSuccess: closeDrawer }
    );
  };

  const withdraw = () => {
    if (!selected) return;
    withdrawMutation.mutate({ id: selected.id }, { onSuccess: closeDrawer });
  };

  const columns: QueueColumn<ApprovalRequest>[] = [
    {
      key: 'requested_at',
      header: 'Requested',
      render: row => (
        <span className="text-gray-text whitespace-nowrap">
          {formatDistanceToNow(new Date(row.requested_at), { addSuffix: true })}
        </span>
      ),
    },
    {
      key: 'operation',
      header: 'Operation',
      render: row => <OperationChip operation={row.operation} />,
    },
    {
      key: 'target',
      header: 'Target',
      render: row => (
        <span className="text-white font-mono text-xs break-all">
          {row.target_label ?? row.target_id}
        </span>
      ),
    },
    {
      key: 'requester',
      header: 'Requested by',
      render: row => (
        <span className="text-gray-text font-mono text-xs break-all">{row.requester_id}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: row => <StatusCell request={row} nowIso={nowIso} />,
    },
    {
      key: 'expires',
      header: 'Expires',
      render: row => <ExpiryCell request={row} nowIso={nowIso} />,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-primary" />
            Content, experiments and files
          </h2>
          <p className="text-sm text-gray-text mt-1">
            Publishing, deleting, activating and archiving from the console. Approving lets the
            requester complete the action from their own console within 15 minutes; nothing runs
            from here. A content revision is approved as the exact content shown in its diff, so the
            requester has 7 days to publish it.
          </p>
        </div>
        <button
          onClick={() => queue.refetch()}
          className="btn-secondary flex items-center space-x-2"
          disabled={queue.isFetching}
        >
          <RefreshCw className={`w-4 h-4 ${queue.isFetching ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      <div className="glass-card p-4 flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-text mr-2">Status:</span>
        {[...STATUS_FILTERS, 'all' as const].map(value => (
          <button
            key={value}
            onClick={() => {
              setStatusFilter(value);
              setSelectedId(null);
            }}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              statusFilter === value
                ? 'bg-primary/20 text-primary'
                : 'bg-gray-500/10 text-gray-text hover:text-white'
            }`}
          >
            {value === 'all' ? 'All' : statusChip(value).label}
          </button>
        ))}
      </div>

      {queue.isError ? (
        <div className="glass-card p-6 border border-red-500/30 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white font-medium mb-1">Failed to load approval requests</p>
            <p className="text-sm text-gray-text mb-3">
              {queue.error instanceof Error ? queue.error.message : 'Unknown error'}
            </p>
            <button onClick={() => queue.refetch()} className="btn-secondary">
              Retry
            </button>
          </div>
        </div>
      ) : (
        <QueueTable
          columns={columns}
          rows={rows}
          rowKey={row => row.id}
          isLoading={queue.isLoading}
          onRowClick={row => setSelectedId(row.id)}
          emptyTitle="No approval requests"
          emptyHint={
            statusFilter === 'all'
              ? 'Nothing from the content, experiment or CDN pages is waiting for review.'
              : `No requests with status "${statusChip(statusFilter).label}".`
          }
        />
      )}

      <DetailDrawer
        open={selected !== null}
        title="Approval request"
        onClose={closeDrawer}
        footer={
          selected &&
          gate &&
          (selectedStatus === 'PENDING' ? (
            <div className="space-y-3">
              {isOwnRequest ? (
                <>
                  <p className="text-sm text-gray-text">{gate.note}</p>
                  <button
                    onClick={withdraw}
                    disabled={withdrawMutation.isPending}
                    className="w-full px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium transition-colors disabled:opacity-50"
                  >
                    {withdrawMutation.isPending ? 'Withdrawing…' : 'Withdraw request'}
                  </button>
                </>
              ) : gate.canDecide ? (
                <>
                  <p className="text-sm text-gray-text">{gate.note}</p>
                  {revisionGate.note && (
                    <p
                      className={`text-sm ${
                        revisionGate.tone === 'crit'
                          ? 'text-red-400'
                          : revisionGate.tone === 'warn'
                            ? 'text-yellow-400'
                            : 'text-gray-text'
                      }`}
                    >
                      {revisionGate.note}
                    </p>
                  )}
                  <ReasonActionBar
                    key={decisionReasons.length}
                    reasons={decisionReasons}
                    actionLabel="Submit decision"
                    isPending={decideMutation.isPending}
                    notePlaceholder="Optional note the requester will see"
                    onSubmit={submitDecision}
                  />
                </>
              ) : (
                <p className="text-sm text-gray-text">{gate.note}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-text">{gate.note}</p>
          ))
        }
      >
        {selected && (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <OperationChip operation={selected.operation} />
              <StatusCell request={selected} nowIso={nowIso} />
              <ExpiryCell request={selected} nowIso={nowIso} />
            </div>

            <div>
              <p className="text-xs text-gray-text uppercase tracking-wide mb-2">
                What the requester will run
              </p>
              <div className="rounded-lg border border-gray-border divide-y divide-gray-border/50 text-sm">
                <div className="px-3 py-2 flex gap-3">
                  <span className="text-xs text-gray-text font-mono w-24 flex-shrink-0">
                    target
                  </span>
                  <span className="text-white font-mono break-all">
                    {selected.target_label ?? selected.target_id}
                  </span>
                </div>
                {selected.target_label && (
                  <div className="px-3 py-2 flex gap-3">
                    <span className="text-xs text-gray-text font-mono w-24 flex-shrink-0">
                      target id
                    </span>
                    <span className="text-white font-mono text-xs break-all">
                      {selected.target_id}
                    </span>
                  </div>
                )}
                <div className="px-3 py-2 flex gap-3">
                  <span className="text-xs text-gray-text font-mono w-24 flex-shrink-0">
                    reason
                  </span>
                  <span className="text-white font-mono break-all">{selected.reason}</span>
                </div>
                {selected.note && (
                  <div className="px-3 py-2 flex gap-3">
                    <span className="text-xs text-gray-text font-mono w-24 flex-shrink-0">
                      note
                    </span>
                    <span className="text-white break-words whitespace-pre-wrap">
                      {selected.note}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {selected.revision && (
              <div>
                <p className="text-xs text-gray-text uppercase tracking-wide mb-2">
                  Proposed content against what is live
                </p>
                <RevisionDiff
                  detail={revisionDetail.data}
                  isLoading={revisionDetail.isLoading}
                  error={revisionDetail.error}
                />
              </div>
            )}

            <div className="space-y-1 text-sm">
              <p className="text-gray-text">
                Requested by{' '}
                <span className="text-white font-mono text-xs break-all">
                  {selected.requester_id}
                </span>{' '}
                {formatDistanceToNow(new Date(selected.requested_at), { addSuffix: true })}
              </p>
              <p className="text-gray-text">
                Request expires {new Date(selected.expires_at).toLocaleString()}
              </p>
              {selected.decider_id && (
                <p className="text-gray-text">
                  Decided by{' '}
                  <span className="text-white font-mono text-xs break-all">
                    {selected.decider_id}
                  </span>
                  {selected.decided_at &&
                    ` ${formatDistanceToNow(new Date(selected.decided_at), { addSuffix: true })}`}
                </p>
              )}
              {selected.decision_note && (
                <p className="text-gray-text">
                  Note: <span className="text-white">{selected.decision_note}</span>
                </p>
              )}
              {selected.approval_expires_at && selectedStatus === 'APPROVED' && (
                <p className="text-gray-text">
                  Completion window closes {new Date(selected.approval_expires_at).toLocaleString()}
                </p>
              )}
              {selected.completed_at && (
                <p className="text-gray-text">
                  Completed{' '}
                  {formatDistanceToNow(new Date(selected.completed_at), { addSuffix: true })}
                </p>
              )}
            </div>
          </>
        )}
      </DetailDrawer>
    </div>
  );
}
